<script setup lang="ts">
  import {
    MpBox,
    MpButton,
    MpContextMenu,
    MpFloating,
    MpIcon,
    MpText,
    openMpConfirmModal,
  } from '@borg/ui';
  import { MyNotification } from '@borg/types';

  const { t } = useI18n();

  const notifications = ref<MyNotification[]>([]);
  const interval = ref<NodeJS.Timeout>();
  const isPopupOpen = ref(false);
  const displayAllNotifications = ref(false);

  const sneakPeakElements = 3;

  const nonDeletedNotifications = computed(() =>
    notifications.value && notifications.value.length
      ? notifications.value.filter((n) => !n.isDeleted)
      : [],
  );

  const visibleNotifications = computed(() =>
    displayAllNotifications.value
      ? nonDeletedNotifications.value
      : nonDeletedNotifications.value.slice(0, sneakPeakElements),
  );

  const hasUnreadNotifications = computed(
    () => !!nonDeletedNotifications.value.find((n) => !n.seenBySubscriber && !n.isDeleted),
  );

  const options = computed(() => {
    const options = [];
    options.push({
      label: t('MY_NOTIFICATIONS.MANAGE'),
      onClick: () => navigate('my-notifications'),
    });
    // if (nonDeletedNotifications.value.length) {
    //   options.push({
    //     label: t('MY_NOTIFICATIONS.DELETE_ALL'),
    //     onClick: () => onDeleteAllNotifications(),
    //   });
    // }
    return options;
  });

  async function fetchNotifications() {
    try {
      notifications.value = await apiFetch<MyNotification[]>(`/ba/notifications`, {
        method: 'GET',
      });
    } catch (error) {
      notifications.value = [];
      removeInterval();
    }
  }

  function displayAll() {
    displayAllNotifications.value = true;
  }

  async function onReadNotification(notification: MyNotification) {
    if (notification.seenBySubscriber) {
      isPopupOpen.value = false;
      return;
    }
    try {
      notification.seenBySubscriber = true;
      await myNotificationsRepository.read(notification.id);
    } catch (error) {
      notification.seenBySubscriber = false;
      console.error(error);
    }
    isPopupOpen.value = false;
  }

  async function onDeleteOneNotification(notification: MyNotification) {
    try {
      notification.isDeleted = true;
      await myNotificationsRepository.delete(notification.id);
    } catch (error) {
      notification.isDeleted = false;
      console.error(error);
    }
  }

  async function onDeleteAllNotifications() {
    const confirmed = await openMpConfirmModal({
      title: t('MY_NOTIFICATIONS.DELETE_ALL_CONFIRM_MODAL.TITLE'),
      text: t('MY_NOTIFICATIONS.DELETE_ALL_CONFIRM_MODAL.TEXT'),
      confirmButtonLabel: t('YES'),
      cancelButtonLabel: t('NO'),
    });

    if (confirmed) {
      const affectedIds = nonDeletedNotifications.value.map((n) => n.id);
      try {
        notifications.value = nonDeletedNotifications.value.map((n) => ({ ...n, isDeleted: true }));
        await myNotificationsRepository.deleteAll();
      } catch (error) {
        notifications.value = notifications.value
          ? notifications.value.map((n) => {
              const isAffected = affectedIds.findIndex((id) => id === n.id) >= 0;
              return { ...n, isDeleted: !isAffected };
            })
          : [];

        console.error(error);
      }
    }
  }

  function removeInterval() {
    if (interval.value) {
      clearInterval(interval.value);
      interval.value = undefined;
    }
  }

  onMounted(() => {
    fetchNotifications();
    interval.value = setInterval(() => {
      fetchNotifications();
    }, 1000 * 120);
  });

  onBeforeUnmount(() => {
    removeInterval();
  });
</script>

<template>
  <div class="my-notifications">
    <MpFloating
      v-model="isPopupOpen"
      :offset="10"
      :show-arrow="false"
      placement="bottom-end"
      @outside-click="isPopupOpen = false"
    >
      <template #default>
        <span
          class="my-notifications__toggle"
          @click="isPopupOpen = !isPopupOpen"
        >
          <MpIcon
            size="lg"
            icon="no_notification"
            class="my-notifications__icon"
          />
          <span
            v-if="hasUnreadNotifications"
            class="my-notifications__dot"
          />
        </span>
      </template>
      <template #content>
        <MpBox
          shadow="deep"
          surface="60"
          class="notifications-popup"
        >
          <div class="notifications-popup__header">
            <MpText
              as="h3"
              no-margin
            >
              {{ $t('MY_NOTIFICATIONS.TITLE') }}
            </MpText>
            <MpContextMenu :options="options" />
          </div>
          <div class="notifications-popup__list">
            <NotificationsItem
              v-for="notification in visibleNotifications"
              :key="notification.id"
              :item="notification"
              @read="onReadNotification"
              @delete="onDeleteOneNotification"
            />
          </div>
          <MpButton
            v-if="!nonDeletedNotifications.length"
            :text="$t('MY_NOTIFICATIONS.NO_NOTIFICATIONS')"
            variant="plain"
            style="pointer-events: none"
            class="notifications-popup__action"
          />
          <MpButton
            v-if="nonDeletedNotifications.length !== visibleNotifications.length"
            :text="$t('MY_NOTIFICATIONS.SHOW_ALL')"
            variant="plain"
            class="notifications-popup__action"
            @click="displayAll"
          />
        </MpBox>
      </template>
    </MpFloating>
  </div>
</template>

<style scoped lang="scss">
  .my-notifications {
    position: relative;

    &__toggle {
      cursor: pointer;
    }

    &__icon {
      color: var(--mp-color-grey-white);
    }

    &__dot {
      position: absolute;
      top: 3px;
      right: 2px;
      width: 3px;
      height: 3px;
      border: 6px solid var(--mp-color-status-warning);
      border-radius: var(--mp-radius-full);
      z-index: 1;
    }
  }

  .notifications-popup {
    max-width: 100%;
    margin: 0 var(--mp-space-60) 0 var(--mp-space-50);
    padding: var(--mp-space-50) 0;
    border-radius: var(--mp-radius-30);

    transition:
      max-width 200ms linear,
      margin 200ms linear;

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 var(--mp-space-70);
      margin-bottom: var(--mp-space-50);
    }

    &__list {
      max-height: calc(2 * var(--mp-space-130));
      overflow: scroll;

      @include screen-sm {
        max-height: calc(3 * var(--mp-space-130));
      }
    }

    &__action {
      display: block;
      margin: var(--mp-space-50) auto 0 auto;
    }

    @include screen-md {
      margin: 0;
      width: calc(2 * var(--mp-space-130) + var(--mp-space-100));
    }
  }
</style>
